import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { ClientListFilters, ClientListResponse } from './types';
import { DEFAULT_CLIENT_FILTERS } from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CLIENTS_KEY = 'clients';
const SEARCH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Build query params from filter state
// ---------------------------------------------------------------------------

function buildParams(filters: ClientListFilters, debouncedSearch: string): Record<string, string> {
  const params: Record<string, string> = {
    page: String(filters.page),
    page_size: String(filters.page_size),
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
  };
  if (debouncedSearch) params.search = debouncedSearch;
  if (filters.kyc_status) params.kyc_status = filters.kyc_status;
  if (filters.status) params.status = filters.status;
  return params;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseClientListReturn {
  data: ClientListResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  filters: ClientListFilters;
  setSearch: (value: string) => void;
  setKycStatus: (value: ClientListFilters['kyc_status']) => void;
  setStatus: (value: ClientListFilters['status']) => void;
  setSorting: (
    sortBy: ClientListFilters['sort_by'],
    sortOrder: ClientListFilters['sort_order'],
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  refresh: () => void;
}

export function useClientList(): UseClientListReturn {
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<ClientListFilters>(DEFAULT_CLIENT_FILTERS);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [filters.search]);

  const params = buildParams(filters, debouncedSearch);

  const query = useQuery<ClientListResponse>({
    queryKey: [CLIENTS_KEY, params],
    queryFn: () => api.get<ClientListResponse>('/api/clients', { params }),
  });

  // Filter setters — reset page to 1 when filters change
  const setSearch = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, search: value, page: 1 }));
  }, []);

  const setKycStatus = useCallback((value: ClientListFilters['kyc_status']) => {
    setFilters((prev) => ({ ...prev, kyc_status: value, page: 1 }));
  }, []);

  const setStatus = useCallback((value: ClientListFilters['status']) => {
    setFilters((prev) => ({ ...prev, status: value, page: 1 }));
  }, []);

  const setSorting = useCallback(
    (sortBy: ClientListFilters['sort_by'], sortOrder: ClientListFilters['sort_order']) => {
      setFilters((prev) => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
    },
    [],
  );

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((size: number) => {
    setFilters((prev) => ({ ...prev, page_size: size, page: 1 }));
  }, []);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
  }, [queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    filters,
    setSearch,
    setKycStatus,
    setStatus,
    setSorting,
    setPage,
    setPageSize,
    refresh,
  };
}
